import { createAction } from '@reduxjs/toolkit';
import { useState } from 'react';

import {
    MARKETPLACE_CARD_APPLICATION_STEPS,
    PERSONAL_DETAILS_STEP,
} from '@perpay-web/fintech/components/screens/MarketplaceCardApplication/MarketplaceCardApplicationSteps';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { useSteps } from '@perpay-web/hooks/useSteps';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

/**
 * Action creator to set the current step in the marketplace card application flow
 * @param {string} step - The step to set as current
 * @returns {import('@reduxjs/toolkit').Action<string>} Redux action for setting the marketplace card application step
 */
export const marketplaceCardApplicationSetSteps = createAction(
    'MARKETPLACE_CARD_APPLICATION_STEPS::SET_STEP',
    (step) => ({ payload: step }),
);

const marketplaceCardApplicationStepsGoBack = createAction(
    'MARKETPLACE_CARD_APPLICATION_STEPS::GO_BACK',
);

const useMarketplaceCardApplication = () => {
    const [showDetailedForm, setShowDetailedForm] = useState(false);
    const [showPhoneDisclosure, setShowPhoneDisclosure] = useState(false);
    const steps = useSteps(
        MARKETPLACE_CARD_APPLICATION_STEPS,
        PERSONAL_DETAILS_STEP,
    );

    useActionObservableCallback(
        hookAction$,
        marketplaceCardApplicationSetSteps().type,
        (step) => steps.setStep(step),
    );

    useActionObservableCallback(
        hookAction$,
        marketplaceCardApplicationStepsGoBack().type,
        () => steps.goBack(),
    );

    return {
        ...steps,
        showDetailedForm,
        setShowDetailedForm,
        showPhoneDisclosure,
        setShowPhoneDisclosure,
    };
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const hook = useMarketplaceCardApplication();

        return <Context.Provider value={hook}>{children}</Context.Provider>;
    };

    return Provider;
};

export const [
    MarketplaceCardApplicationProvider,
    useMarketplaceCardApplicationContext,
] = createContextProvider(providerFactory);
